// Aktionen je Bestellposition (21.10.2026 Schnitt 3 „alles in der Maske“): Hinweise, Bedarf an den
// Grundartikel, Loeschen sowie Menge und EK. Reiter Positionen der Akte und Arbeitsplatz nutzen
// dieselben Bausteine; geschrieben wird nur ueber die bestehenden Item-Routen, die Pruefungen
// (Preisrecht, Archiv, Spaltengrenzen) macht weiter der Server.
import { useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUpToLine, Trash2 } from "lucide-react";
import type { PurchaseOrderItem } from "@/api/purchase";
import { useDeletePurchaseItem, usePatchPurchaseItem, useTransferRequirementToGa } from "@/api/purchase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { fehlerText } from "@/lib/fehlerText";
import { jeEinheit } from "./einheit";

const err = (rueckfall: string) => (e: unknown) => toast.error(fehlerText(e, rueckfall));

/** Kennung einer Position in Feldnamen und Meldungen (wie bisher im Reiter Positionen). */
export const positionsName = (item: PurchaseOrderItem) => item.merchant_sku ?? String(item.id);

/** Spalte numeric(12,4) wie `GRENZE_PREIS` im Server: kleiner 100 Mio., hoechstens vier Nachkommastellen. */
const MAX_EK = 1e8;
const MAX_MENGE = 2147483647;

/**
 * EK in USD lesen. Wie `parseBetrag`, aber mit bis zu VIER Nachkommastellen (EK je Stueck,
 * z. B. 1,466) und ohne Runden: was nicht genau passt, ist unlesbar (`undefined`).
 * `"1.466"` ohne Komma ist mehrdeutig (1466 mit Tausenderpunkt oder 1,466) — beim EK liegt
 * genau dort der Faktor 1000, deshalb wird nicht geraten. Leer = `null`.
 */
export function parseEkUsd(s: string): number | null | undefined | "mehrdeutig" {
  const roh = s.trim();
  if (roh === "") return null;
  if (!/^[0-9.,]+$/.test(roh)) return undefined;
  let normal: string;
  if (roh.includes(",")) {
    if (!/^\d{1,3}(\.\d{3})*,\d{1,4}$/.test(roh) && !/^\d+,\d{1,4}$/.test(roh)) return undefined;
    normal = roh.replace(/\./g, "").replace(",", ".");
  } else if (/^\d{1,3}\.\d{3}$/.test(roh)) {
    return "mehrdeutig";
  } else if (roh.includes(".")) {
    if (!/^\d+\.\d{1,4}$/.test(roh)) return undefined;
    normal = roh;
  } else {
    normal = roh;
  }
  const wert = Number(normal);
  if (!Number.isFinite(wert) || wert < 0 || wert >= MAX_EK) return undefined;
  return wert;
}

/** Gespeicherten EK so ins Feld schreiben, dass `parseEkUsd` ihn unveraendert wieder liest. */
export function ekEingabe(v: number | null): string {
  return v === null ? "" : v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 4, useGrouping: false });
}

const gleicherEk = (a: number | null, b: number | null) =>
  a === null || b === null ? a === b : Math.round(a * 1e4) === Math.round(b * 1e4);

type NotizFeld = "anforderungs_notiz" | "reklamation" | "reklamation_loesung";
const NOTIZ_LABEL: Record<NotizFeld, string> = { anforderungs_notiz: "Anforderung", reklamation: "Reklamation", reklamation_loesung: "Lösung" };

/** Hinweis je Position; speichert beim Verlassen, nur wenn sich der Text geaendert hat. */
export function PositionNotiz({ item, feld, className = "min-w-40 text-xs" }: {
  item: PurchaseOrderItem;
  feld: NotizFeld;
  className?: string;
}) {
  const patchItem = usePatchPurchaseItem();
  const gespeichert = item[feld] ?? "";
  return (
    <Textarea
      // Neuer Stand vom Server (z. B. aus der anderen Ansicht derselben Seite) baut das Feld neu auf.
      key={gespeichert}
      defaultValue={gespeichert}
      rows={1}
      className={className}
      aria-label={`${NOTIZ_LABEL[feld]} ${positionsName(item)}`}
      onBlur={(e) => {
        const v = e.target.value.trim();
        if (v !== gespeichert) {
          patchItem.mutate({ id: item.id, body: { [feld]: v || null } }, { onError: err("Speichern fehlgeschlagen") });
        }
      }}
    />
  );
}

/** E19: Anforderung der Position dauerhaft am Grundartikel speichern. */
export function AnforderungAnGaKnopf({ item, mitText = false }: { item: PurchaseOrderItem; mitText?: boolean }) {
  const transferGa = useTransferRequirementToGa();
  return (
    <Button
      type="button"
      variant={mitText ? "outline" : "ghost"}
      size="sm"
      className={mitText ? "h-7 gap-1.5 px-2 text-xs" : "size-7 p-0"}
      aria-label={`Anforderung dauerhaft an Grundartikel ${positionsName(item)}`}
      title="Anforderung dauerhaft am Grundartikel speichern (E19)"
      disabled={!item.anforderungs_notiz || transferGa.isPending}
      onClick={() =>
        transferGa.mutate(item.id, {
          onSuccess: () => toast.success("Anforderung am Grundartikel gespeichert"),
          onError: err("Übertrag fehlgeschlagen"),
        })
      }
    >
      <ArrowUpToLine className="size-3.5" />
      {mitText ? "Anforderung → Grundartikel" : null}
    </Button>
  );
}

/** Position loeschen; mit `bestaetigen` erst nach der Rueckfrage direkt am Knopf. */
export function PositionLoeschenKnopf({ item, bestaetigen = false, mitText = false }: {
  item: PurchaseOrderItem;
  bestaetigen?: boolean;
  mitText?: boolean;
}) {
  const deleteItem = useDeletePurchaseItem();
  const [frage, setFrage] = useState(false);
  const name = positionsName(item);
  const loeschen = () =>
    deleteItem.mutate(item.id, {
      onSuccess: () => toast.success("Position gelöscht"),
      onError: err("Löschen fehlgeschlagen"),
      onSettled: () => setFrage(false),
    });
  if (frage) {
    return (
      <span role="group" aria-label={`Position ${name} löschen?`} className="inline-flex flex-wrap items-center gap-1.5 text-xs">
        <span className="font-semibold text-coral">Position {name} wirklich löschen?</span>
        <Button type="button" size="sm" variant="destructive" className="h-7 px-2 text-xs"
          aria-label={`Ja, Position ${name} löschen`} disabled={deleteItem.isPending} onClick={loeschen}>
          Löschen
        </Button>
        <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setFrage(false)}>
          Nicht löschen
        </Button>
      </span>
    );
  }
  return (
    <Button
      type="button"
      variant={mitText ? "outline" : "ghost"}
      size="sm"
      className={mitText ? "h-7 gap-1.5 px-2 text-xs text-coral" : "size-7 p-0"}
      aria-label={`Position löschen ${name}`}
      disabled={deleteItem.isPending}
      onClick={() => (bestaetigen ? setFrage(true) : loeschen())}
    >
      <Trash2 className="size-3.5 text-coral" />
      {mitText ? "Position löschen" : null}
    </Button>
  );
}

/**
 * Menge und (mit Preisrecht) EK je Position aendern. Gesendet wird nur, was sich geaendert hat;
 * ohne `mitPreis` bleibt der EK ganz aus dem Body. Fehler stehen am Feld, ein Riegel verhindert
 * Doppelklicks. Container-Positionen (Packliste) und Misch-EK bleiben unberuehrt.
 */
export function PositionMengeEk({ item, mitPreis }: { item: PurchaseOrderItem; mitPreis: boolean }) {
  const patchItem = usePatchPurchaseItem();
  const [menge, setMenge] = useState(String(item.qty));
  const [ek, setEk] = useState(ekEingabe(item.unit_price_usd));
  const [fehler, setFehler] = useState<string | null>(null);
  // Serverstand, auf dem die Felder beruhen. Kommt ein neuer (andere Ansicht, Refetch), folgen
  // unberuehrte Felder ihm; nur Bearbeitetes bleibt stehen. Sonst ginge ein alter EK mit raus.
  // Unberuehrt heisst: der Wert ist gleich der Basis, nicht die Schreibweise („2“ = „2,00“).
  const [basis, setBasis] = useState({ qty: item.qty, ek: item.unit_price_usd });
  if (item.qty !== basis.qty || !gleicherEk(item.unit_price_usd, basis.ek)) {
    const m = menge.trim();
    if (/^\d+$/.test(m) && Number(m) === basis.qty) setMenge(String(item.qty));
    const p = parseEkUsd(ek);
    if ((p === null || typeof p === "number") && gleicherEk(p, basis.ek)) setEk(ekEingabe(item.unit_price_usd));
    setBasis({ qty: item.qty, ek: item.unit_price_usd });
  }
  const riegel = useRef(false);
  const name = positionsName(item);

  const speichern = () => {
    if (riegel.current) return;
    setFehler(null);
    const m = menge.trim();
    const qty = /^\d+$/.test(m) ? Number(m) : NaN;
    if (!Number.isSafeInteger(qty) || qty <= 0 || qty > MAX_MENGE) return setFehler("Menge: ganze Zahl größer 0");
    const body: Record<string, unknown> = {};
    if (qty !== item.qty) body.qty = qty;
    if (mitPreis) {
      const preis = parseEkUsd(ek);
      if (preis === "mehrdeutig") return setFehler(`EK „${ek.trim()}“ ist mehrdeutig: mit Dezimalkomma schreiben (z. B. 1,466)`);
      if (preis === undefined) return setFehler("EK nicht lesbar — Zahl mit höchstens vier Nachkommastellen, z. B. 1,466");
      if (preis === null && item.unit_price_usd !== null) return setFehler("EK eintragen");
      if (!gleicherEk(preis, item.unit_price_usd)) body.unit_price_usd = preis;
    }
    if (Object.keys(body).length === 0) return;
    riegel.current = true;
    patchItem.mutate({ id: item.id, body }, {
      onSuccess: () => toast.success(`Position ${name} gespeichert`),
      onError: (e) => setFehler(fehlerText(e, "Speichern fehlgeschlagen")),
      onSettled: () => { riegel.current = false; },
    });
  };

  return (
    <div className="grid gap-1.5">
      <div className="flex flex-wrap items-end gap-2">
        <label className="grid gap-1 text-[11px] text-ink-2">
          Menge{item.einheit === "meter" ? " (m)" : ""}
          <Input value={menge} inputMode="numeric" aria-label={`Menge ${name}`} className="h-8 w-28 font-mono text-sm"
            onChange={(e) => setMenge(e.target.value)} />
        </label>
        {mitPreis ? (
          <label className="grid gap-1 text-[11px] text-ink-2">
            EK USD{jeEinheit(item.einheit)}
            <Input value={ek} inputMode="decimal" aria-label={`EK (USD) ${name}`} className="h-8 w-32 font-mono text-sm"
              onChange={(e) => setEk(e.target.value)} />
          </label>
        ) : null}
        <Button type="button" size="sm" disabled={patchItem.isPending} onClick={speichern}
          aria-label={mitPreis ? `Menge und EK speichern für ${name}` : `Menge speichern für ${name}`}>
          Speichern
        </Button>
      </div>
      {fehler ? <p role="alert" className="text-xs text-coral">{fehler}</p> : null}
      <p className="text-[11px] leading-snug text-ink-2">
        Ändert nur die Bestellposition. Packlisten-Werte der Container und der Misch-EK bleiben unverändert.
      </p>
    </div>
  );
}
